import {
  createSelector,
} from 'reselect';
import i18n from 'i18next';

import {
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
  getFeatures,
} from './ui';

export const getSidebarProFolders = createSelector(
  [
    coreSelectors.getProfile,
    coreSelectors.getTreeFoldersWithItems('exercises'),
    coreSelectors.getTreeFoldersWithItems('documents'),
    coreSelectors.getTreeFoldersWithItems('workoutTemplates'),
    coreSelectors.getTreeFoldersWithItems('programTemplates'),
    coreSelectors.getResourceMeta('conversations', 'unread'),
    getUiState('namespace'),
    getFeatures,
  ],
  (
    profile,
    exercises,
    documents,
    workoutTemplates,
    programTemplates,
    unreadChat,
    namespace,
    features,
  ) => {
    const isNotPremium = profile.subscription_status !== 'Training' && profile.subscription_status !== 'Active' && profile.subscription_status !== 'Canceled'

    const items = [
      {
        icon: 'dashboard',
        link: 'welcome',
        name: 'Dashboard',
      },
      {
        icon: 'clients',
        link: 'clients/individuals',
        name: i18n.format(i18n.t('client_plural'), 'cap'),
        additionalPaths: [
          'users',
          'client_groups',
          'personal_programs',
          'personal_workouts',
        ],
        children: [
          {
            icon: 'clients',
            link: 'clients/individuals',
            name: 'Individuals',
          },
          {
            icon: 'groups',
            link: 'clients/groups',
            name: 'Groups',
          },
          {
            icon: 'clients',
            link: 'clients/archived',
            name: 'Archived',
          },
        ],
      },
      {
        icon: 'messages',
        link: 'messages',
        name: 'Messages',
        badge: unreadChat?.count > 0 ? unreadChat.count : null,
        isHidden: !features.messages,
        requiresPremium: namespace.symbol === 'ideafit' && isNotPremium,
      },
      {
        icon: 'calendar',
        link: 'calendar',
        name: 'Calendar',
        isHidden: !features.calendar_and_scheduling,
        requiresPremium: namespace.symbol === 'ideafit' && isNotPremium,
      },
      {
        icon: 'billing',
        link: 'billing/invoices',
        name: 'Billing',
        isHidden: !features.billing_payment,
      },
      {
        ...(exercises || { id: '0' }),
        icon: 'exercises',
        link: 'exercises',
        name: 'Exercises',
        isFolder: true,
      },
      {
        ...(workoutTemplates || { id: '0' }),
        icon: 'workout2',
        link: 'workout_templates',
        name: i18n.t('workoutTemplates'),
        isFolder: true,
      },
      {
        ...(programTemplates || { id: '0' }),
        icon: 'programs',
        link: 'program_templates',
        name: 'Program Templates',
        isFolder: true,
      },
      {
        icon: 'videogallery',
        link: 'videos/recent',
        name: 'Video Library',
        isHidden: !features.video_library,
        requiresPremium: namespace.symbol === 'ideafit' && isNotPremium,
      },
      {
        ...(documents || { id: '0' }),
        icon: 'documents',
        link: 'documents',
        name: 'Documents',
        isFolder: true,
        isHidden: !features.documents,
        requiresPremium: namespace.symbol === 'ideafit' && isNotPremium,
      },
      {
        icon: 'trainings',
        link: 'users/me/programs',
        name: 'My Training Log',
      },
      {
        icon: 'properties',
        link: 'personal_properties',
        name: 'Metrics',
      },
      {
        icon: 'marketplace',
        link: 'marketplace',
        name: 'Marketplace',
        isHidden: namespace.symbol === 'gymcloud' || !features.billing_payment,
      },
    ];

    return items.filter(item => item.requiresPremium || !item.isHidden);
  },
);

export const getSidebarClientFolders = createSelector(
  [
    coreSelectors.getFolderItemsSortedByName('personalExercises'),
    coreSelectors.getFolderItemsSortedByName('personalWarmups'),
    coreSelectors.getFolderItemsSortedByName('personalWorkouts'),
    coreSelectors.getFolderItemsSortedByName('personalCooldowns'),
    coreSelectors.getFolderItemsSortedByName('personalPrograms'),
    getUiState('namespace'),
    getFeatures,
  ],
  (
    personalExercises,
    personalWarmups,
    personalWorkouts,
    personalCooldowns,
    personalPrograms,
    namespace,
    features,
  ) => {
    const items = [
      {
        icon: 'dashboard',
        link: 'welcome',
        name: 'Dashboard',
      },
      {
        icon: 'exercises',
        link: 'personal_exercises',
        name: 'Exercises',
        children: personalExercises,
      },
      {
        icon: 'warmups',
        link: 'personal_warmups',
        name: 'Warmups',
        children: personalWarmups,
      },
      {
        icon: 'workout2',
        link: 'personal_workouts',
        name: i18n.t('Workouts'),
        children: personalWorkouts,
      },
      {
        icon: 'cooldowns',
        link: 'personal_cooldowns',
        name: 'Cooldowns',
        children: personalCooldowns,
      },
      {
        icon: 'programs',
        link: 'personal_programs',
        name: 'Programs',
        children: personalPrograms,
      },
      {
        icon: 'videogallery',
        link: 'videos/recent',
        name: 'Video Library',
        isHidden: !features.video_library,
      },
      {
        icon: 'documents',
        link: 'documents',
        name: 'Documents',
        isHidden: !features.documents,
      },
      {
        icon: 'marketplace',
        link: 'marketplace',
        name: 'Marketplace',
        isHidden: namespace.symbol === 'gymcloud' || !features.billing_payment,
      },
    ];

    return items.filter(item => !item.isHidden);
  },
);
